import 'intersection-observer'
import { debounce, DebouncedFunc } from 'lodash'
import { canParseToInteger, getInteger } from '@/utils/numberExtension'

interface LazyOptions {
  lazy: number | string
  imageSrc: string
  defaultSrc?: string
}

class CachePool {
  _imagePayload: Map<string, boolean>

  constructor () {
    this._imagePayload = new Map()
  }

  add (imageUrl) {
    if (this._imagePayload.has(imageUrl)) {
      return
    }
    this._imagePayload.set(imageUrl, false)
  }

  get (imageUrl) {
    return this._imagePayload.get(imageUrl)
  }

  markDone (imageUrl) {
    this._imagePayload.set(imageUrl, true)
  }
}

type Updater = (entry: IntersectionObserverEntry) => void;

const isIntersectingDisplayStrategy = (
  updater: Updater
): DebouncedFunc<Updater> =>
  debounce((entry) => {
    if (!entry.isIntersecting) return
    updater(entry)
  }, 300)

interface WatchHandlerConfig {
  onDisplay: (element) => void;
}

class WatchHandler {
  _intersectionObserverMap: WeakMap<HTMLImageElement, IntersectionObserver>
  _onDisplayCallback: (element) => void

  constructor ({ onDisplay }: WatchHandlerConfig) {
    if (typeof onDisplay !== 'function') {
      throw new Error('Invalid parameters.')
    }

    this._intersectionObserverMap = new WeakMap()
    this._onDisplayCallback = onDisplay
  }

  add (el, distance) {
    if (this._intersectionObserverMap.has(el)) return
    const simpleUpdater = (entry) => {
      const { target } = entry
      this._onDisplayCallback(target)
      this.delete(target)
    }
    const updater = isIntersectingDisplayStrategy((entry) => {
      simpleUpdater(entry)
      updater.cancel()
    })
    const _intersectionObserver = new IntersectionObserver(
      ([entry]) => updater(entry),
      {
        rootMargin: `${distance}px`
      }
    )
    _intersectionObserver.observe(el)
    this._intersectionObserverMap.set(el, _intersectionObserver)
  }

  delete (el) {
    const _intersectionObserver = this._intersectionObserverMap.get(el)
    if (_intersectionObserver) {
      _intersectionObserver.unobserve(el)
      _intersectionObserver.disconnect()
      this._intersectionObserverMap.delete(el)
    }
  }
}

const loadImage = (src) => {
  if (!src) return Promise.resolve('')

  return new Promise((resolve, reject) => {
    const _img = new Image()
    _img.addEventListener('load', () => resolve(src))
    _img.addEventListener('error', reject)
    _img.src = src
  })
}

const assignImage = (el, src, defaultSrc) => {
  loadImage(src)
    .catch(() => defaultSrc)
    .then(resultSrc => { el.src = resultSrc ?? '' })
}

export interface LazyConfig {
  preload?: boolean;
  srcModifier?: (originSrc: string, element) => string;
}

export default class LazyLoad {
  _watchHandler: WatchHandler
  _cache: CachePool
  _srcModifier: (originSrc: string, element) => string

  constructor ({ srcModifier }: LazyConfig = {}) {
    if (typeof window === 'undefined') {
      return
    }

    this._cache = new CachePool()

    this._watchHandler = new WatchHandler({
      onDisplay: (element: HTMLElement) => {
        assignImage(element, element.dataset.src, element.dataset.defaultSrc)
        this._cache.markDone(element.dataset.src)
      }
    })

    this._srcModifier = srcModifier
  }

  register (el: HTMLImageElement, options: LazyOptions) {
    if (!el) return
    if (el.tagName !== 'IMG') {
      throw new Error('v-lazy can only be used on <img> tag.')
    }

    const defaultSrc = options.defaultSrc
    const src =
      typeof this._srcModifier === 'function'
        ? this._srcModifier(options.imageSrc, el)
        : (options.imageSrc ?? defaultSrc)
    if (!src || typeof src !== 'string') {
      el.src = ''
      return
    }
    if (el.src === src) {
      return
    }

    el.dataset.src = src
    el.dataset.defaultSrc = defaultSrc

    if (this._cache.get(src)) {
      el.src = src
      return
    }

    // set default image
    if (defaultSrc) el.src = defaultSrc

    // optionally lazy load image if an argument value is provided
    // e.g <LazyImage lazy=300 src={src} />
    const lazyThreshold = options.lazy
    if (lazyThreshold && canParseToInteger(lazyThreshold)) {
      const distance = getInteger(lazyThreshold)

      // append element to watchHandler, let it keep track of the distance threshold
      this._watchHandler.add(el, distance)
      this._cache.add(src)
    } else {
      assignImage(el, src, defaultSrc)
    }
  }

  unregister (el) {
    this._watchHandler.delete(el)
  }
}
